'use client';

import Link from 'next/link';
import { useRouter, useSearchParams } from 'next/navigation';
import {
  type FormEvent,
  useCallback,
  useEffect,
  useMemo,
  type CSSProperties,
  useState,
} from 'react';
import SocialAuthButtons from '@/components/auth/SocialAuthButtons';
import Button from '@/components/ui/Button';
import { apiClient } from '@/lib/api';
import { clearAuth, getAccessToken, setAuth, setStoredUser } from '@/lib/authStorage';
import { cn } from '@/lib/utils';

type AuthMode = 'login' | 'register';
type RegisterStep = 'email' | 'verify' | 'done';

type AuthExperienceProps = {
  mode: AuthMode;
};

type AuthCapabilities = {
  legacy_login_enabled: boolean;
  email_registration_disabled: boolean;
  social_auth_enabled: boolean;
  social_providers: { google: boolean; vk: boolean; telegram: boolean; max: boolean };
};

type AuthFeaturePrompt = {
  label: string;
  text: string;
  desktopTop: string;
  desktopLeft: string;
  desktopRight?: string;
  mobileTop: string;
  mobileLeft: string;
  mobileRight?: string;
  size: 'sm' | 'md' | 'lg';
  depth: 'far' | 'mid' | 'front';
  rotate: number;
  delayMs: number;
  durationS: number;
};

const DEFAULT_CAPABILITIES: AuthCapabilities = {
  legacy_login_enabled: true,
  email_registration_disabled: false,
  social_auth_enabled: false,
  social_providers: { google: false, vk: false, telegram: false, max: false },
};

function normalizeReturnTo(value: string | null): string {
  const candidate = (value || '').trim();
  if (!candidate || !candidate.startsWith('/') || candidate.startsWith('//')) return '/';
  if (candidate.startsWith('/login') || candidate.startsWith('/register')) return '/';
  return candidate;
}

const panelInputClass =
  'w-full rounded-2xl border border-site-border bg-site-text/6 px-4 py-3 text-sm text-site-text outline-none transition-all duration-300 placeholder:text-site-muted focus:border-site-accent/60 focus:bg-site-text/9 focus:shadow-[0_0_0_1px_rgba(103,232,249,0.35),0_0_32px_rgba(34,211,238,0.14)]';

const AUTH_FEATURE_PROMPTS: AuthFeaturePrompt[] = [
  {
    label: 'STEM-мессенджер',
    text: 'Личные и командные чаты для быстрых обсуждений и рабочих каналов.',
    desktopTop: '8%',
    desktopLeft: '3%',
    mobileTop: '2%',
    mobileLeft: '4%',
    size: 'md',
    depth: 'front',
    rotate: -6,
    delayMs: 0,
    durationS: 11.5,
  },
  {
    label: 'Проекты',
    text: 'Создавайте исследования, собирайте команду и ведите структуру проекта по этапам.',
    desktopTop: '15%',
    desktopLeft: 'auto',
    desktopRight: '2%',
    mobileTop: '10%',
    mobileLeft: 'auto',
    mobileRight: '4%',
    size: 'lg',
    depth: 'front',
    rotate: 8,
    delayMs: 420,
    durationS: 15.5,
  },
  {
    label: 'Профиль',
    text: 'Резюме, портфолио, публикации и научные карточки в одном рабочем профиле.',
    desktopTop: '70%',
    desktopLeft: '4%',
    mobileTop: '79%',
    mobileLeft: '4%',
    size: 'md',
    depth: 'mid',
    rotate: 5,
    delayMs: 1180,
    durationS: 17,
  },
  {
    label: 'Игры',
    text: 'Neural Runner и другие мини-игры лаборатории для STEM-среды.',
    desktopTop: '58%',
    desktopLeft: 'auto',
    desktopRight: '3%',
    mobileTop: '86%',
    mobileLeft: 'auto',
    mobileRight: '5%',
    size: 'sm',
    depth: 'far',
    rotate: -8,
    delayMs: 180,
    durationS: 14.2,
  },
  {
    label: 'Новости и сообщество',
    text: 'Следите за событиями лаборатории, обновлениями и новыми точками входа в команду.',
    desktopTop: '74%',
    desktopLeft: 'auto',
    desktopRight: '8%',
    mobileTop: '92%',
    mobileLeft: '8%',
    size: 'sm',
    depth: 'mid',
    rotate: -4,
    delayMs: 840,
    durationS: 16.5,
  },
  {
    label: 'Вакансии и участие',
    text: 'Открывайте вакансии, стажировки и подключайтесь к инициативам ЛИСАД.',
    desktopTop: '45%',
    desktopLeft: '2%',
    mobileTop: '70%',
    mobileLeft: '50%',
    size: 'sm',
    depth: 'far',
    rotate: -2,
    delayMs: 640,
    durationS: 15.2,
  },
];

function OrbitIcon(props: { className?: string }) {
  return (
    <svg viewBox="0 0 24 24" fill="none" className={props.className} aria-hidden="true">
      <circle cx="12" cy="12" r="2.2" fill="currentColor" />
      <ellipse cx="12" cy="12" rx="8" ry="3.8" stroke="currentColor" strokeWidth="1.4" />
      <ellipse
        cx="12"
        cy="12"
        rx="8"
        ry="3.8"
        stroke="currentColor"
        strokeWidth="1.4"
        transform="rotate(55 12 12)"
      />
    </svg>
  );
}

function ShieldIcon(props: { className?: string }) {
  return (
    <svg viewBox="0 0 24 24" fill="none" className={props.className} aria-hidden="true">
      <path
        d="M12 3L19 6V11.2C19 15.75 16.02 19.95 12 21C7.98 19.95 5 15.75 5 11.2V6L12 3Z"
        stroke="currentColor"
        strokeWidth="1.4"
        strokeLinejoin="round"
      />
      <path d="M9.6 12.1L11.25 13.75L14.6 10.4" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" />
    </svg>
  );
}

export default function AuthExperience({ mode }: AuthExperienceProps) {
  const router = useRouter();
  const searchParams = useSearchParams();

  const [capabilities, setCapabilities] = useState<AuthCapabilities>(DEFAULT_CAPABILITIES);
  const [loginData, setLoginData] = useState({ username: '', password: '' });
  const [rememberMe, setRememberMe] = useState(true);
  const [loginLoading, setLoginLoading] = useState(false);
  const [loginError, setLoginError] = useState('');
  const [showLoginPassword, setShowLoginPassword] = useState(false);

  const [registerStep, setRegisterStep] = useState<RegisterStep>('email');
  const [registerEmail, setRegisterEmail] = useState('');
  const [registerToken, setRegisterToken] = useState('');
  const [registerCode, setRegisterCode] = useState('');
  const [registerPassword, setRegisterPassword] = useState('');
  const [registerPasswordConfirm, setRegisterPasswordConfirm] = useState('');
  const [registerLoading, setRegisterLoading] = useState(false);
  const [registerError, setRegisterError] = useState('');
  const [registerSuccess, setRegisterSuccess] = useState('');
  const [registeredUsername, setRegisteredUsername] = useState('');
  const [showRegisterPassword, setShowRegisterPassword] = useState(false);

  const socialError = (searchParams.get('social_error') || '').trim();
  const returnTo = useMemo(() => normalizeReturnTo(searchParams.get('return_to')), [searchParams]);

  const labTitle = 'ЛИСАД';

  useEffect(() => {
    const token = getAccessToken();
    if (token) router.replace(returnTo);
  }, [returnTo, router]);

  useEffect(() => {
    let alive = true;
    const loadCapabilities = async () => {
      try {
        const data = await apiClient.getLegacyLoginStatus();
        if (alive) {
          setCapabilities({
            ...DEFAULT_CAPABILITIES,
            ...data,
            social_providers: data.social_providers || DEFAULT_CAPABILITIES.social_providers,
          });
        }
      } catch {
        if (alive) setCapabilities(DEFAULT_CAPABILITIES);
      }
    };

    void loadCapabilities();
    return () => {
      alive = false;
    };
  }, []);

  const hasModeEmailChannel = useMemo(
    () => (mode === 'login' ? capabilities.legacy_login_enabled : !capabilities.email_registration_disabled),
    [capabilities.email_registration_disabled, capabilities.legacy_login_enabled, mode]
  );

  const handleLoginSubmit = useCallback(
    async (event: FormEvent) => {
      event.preventDefault();
      if (!loginData.username.trim() || !loginData.password.trim()) {
        setLoginError('Введите логин/email и пароль.');
        return;
      }

      setLoginLoading(true);
      setLoginError('');
      try {
        const data = await apiClient.login(loginData.username.trim(), loginData.password);
        setAuth({ access: data.access, refresh: data.refresh, user: data.user }, rememberMe);
        if (!data.user) {
          try {
            const user = await apiClient.getCurrentUser();
            setStoredUser(user);
          } catch {
            // ignore
          }
        }
        router.replace(returnTo);
      } catch (error: any) {
        clearAuth();
        setLoginError(error?.message || 'Не удалось выполнить вход.');
      } finally {
        setLoginLoading(false);
      }
    },
    [loginData.password, loginData.username, rememberMe, returnTo, router]
  );

  const handleRegisterCodeRequest = useCallback(
    async (event: FormEvent) => {
      event.preventDefault();
      const normalizedEmail = registerEmail.trim();
      if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(normalizedEmail)) {
        setRegisterError('Введите корректный email.');
        return;
      }

      setRegisterLoading(true);
      setRegisterError('');
      setRegisterSuccess('');
      try {
        const response = await apiClient.requestRegisterCode(normalizedEmail);
        setRegisterToken(response.token || '');
        setRegisterStep('verify');
        setRegisterSuccess('Код подтверждения отправлен. Проверьте почту.');
      } catch (error: any) {
        setRegisterError(error?.message || 'Не удалось отправить код подтверждения.');
      } finally {
        setRegisterLoading(false);
      }
    },
    [registerEmail]
  );

  const handleRegisterSubmit = useCallback(
    async (event: FormEvent) => {
      event.preventDefault();
      if (!registerToken.trim()) {
        setRegisterError('Сначала запросите код подтверждения.');
        return;
      }
      if (!/^\d{6}$/.test(registerCode.trim())) {
        setRegisterError('Код должен состоять из 6 цифр.');
        return;
      }
      if (registerPassword.length < 8) {
        setRegisterError('Пароль должен содержать минимум 8 символов.');
        return;
      }
      if (registerPassword !== registerPasswordConfirm) {
        setRegisterError('Пароли не совпадают.');
        return;
      }

      setRegisterLoading(true);
      setRegisterError('');
      setRegisterSuccess('');
      try {
        const response = await apiClient.register({
          email: registerEmail.trim(),
          token: registerToken.trim(),
          code: registerCode.trim(),
          password: registerPassword,
          password_confirm: registerPasswordConfirm,
        });
        setRegisteredUsername(response.username || '');
        setRegisterStep('done');
        setRegisterSuccess('Аккаунт создан. Пароль по email не отправляется — используйте тот, который задали при регистрации.');
      } catch (error: any) {
        setRegisterError(error?.message || 'Не удалось завершить регистрацию.');
      } finally {
        setRegisterLoading(false);
      }
    },
    [registerCode, registerEmail, registerPassword, registerPasswordConfirm, registerToken]
  );

  return (
    <div className="relative min-h-screen overflow-hidden bg-site-bg text-site-text">

      <div className="pointer-events-none absolute inset-0 bg-site-action/[0.025]" />
      <div className="auth-grid absolute inset-0 opacity-50" />

      <div className="relative z-10 min-h-screen">
        <div className="mx-auto flex min-h-screen w-full max-w-[1320px] items-center px-4 py-6 sm:px-6 lg:px-8 lg:py-10">
          <section className="auth-stage relative mx-auto w-full max-w-[1180px]">
            <div aria-hidden="true" className="auth-feature-layer">
              {AUTH_FEATURE_PROMPTS.map((prompt) => (
                <span
                  key={prompt.label}
                  className={cn(
                    'auth-feature-note',
                    `auth-feature-note--${prompt.size}`,
                    `auth-feature-note--${prompt.depth}`
                  )}
                  style={
                    {
                      '--auth-note-top': prompt.desktopTop,
                      '--auth-note-left': prompt.desktopLeft,
                      '--auth-note-right': prompt.desktopRight,
                      '--auth-note-mobile-top': prompt.mobileTop,
                      '--auth-note-mobile-left': prompt.mobileLeft,
                      '--auth-note-mobile-right': prompt.mobileRight,
                      '--auth-note-rotate': `${prompt.rotate}deg`,
                      '--auth-note-delay': `${prompt.delayMs}ms`,
                      '--auth-note-duration': `${prompt.durationS}s`,
                    } as CSSProperties
                  }
                >
                  <span className="auth-feature-note__label">{prompt.label}</span>
                  <span className="auth-feature-note__text">{prompt.text}</span>
                </span>
              ))}
            </div>

            <div className="auth-console relative z-10 mx-auto w-full max-w-[620px] overflow-hidden rounded-[32px] border border-site-border bg-site-surface p-4 shadow-[0_24px_120px_rgba(0,0,0,0.52)] backdrop-blur-2xl sm:p-5 lg:p-6">
              <div className="relative z-10">
                <div className="mb-5 flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <div className="flex flex-wrap items-center gap-2.5">
                      <span className="inline-flex items-center rounded-full border border-site-accent/24 bg-site-accent/10 px-3 py-1 text-sm font-semibold text-site-accent">
                        {labTitle}
                      </span>
                    </div>
                  </div>
                  <span className="inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-2xl border border-site-accent/15 bg-site-accent/8 text-site-accent">
                    <OrbitIcon className="h-5 w-5" />
                  </span>
                </div>

                <div className="mb-6 grid grid-cols-2 gap-2 rounded-[24px] border border-site-border bg-site-surface/[0.035] p-1.5">
                  <Link
                    href="/login"
                    className={cn(
                      'rounded-[18px] px-4 py-3 text-center text-sm font-medium transition-all',
                      mode === 'login'
                        ? 'bg-site-surface text-site-text shadow-[0_12px_36px_rgba(34,211,238,0.16)]'
                        : 'text-site-muted hover:text-site-text'
                    )}
                  >
                    Вход
                  </Link>
                  <Link
                    href="/register"
                    className={cn(
                      'rounded-[18px] px-4 py-3 text-center text-sm font-medium transition-all',
                      mode === 'register'
                        ? 'bg-site-surface text-site-text shadow-[0_12px_36px_rgba(45,212,191,0.14)]'
                        : 'text-site-muted hover:text-site-text'
                    )}
                  >
                    Регистрация
                  </Link>
                </div>

                {(socialError || loginError || registerError || registerSuccess) && (
                  <div className="mb-5 space-y-3">
                    {socialError && <div className="rounded-2xl border border-site-danger/22 bg-site-danger/10 px-4 py-3 text-sm text-site-danger">Ошибка social-входа: {socialError}</div>}
                    {loginError && mode === 'login' && <div className="rounded-2xl border border-site-danger/22 bg-site-danger/10 px-4 py-3 text-sm text-site-danger">{loginError}</div>}
                    {registerError && mode === 'register' && <div className="rounded-2xl border border-site-danger/22 bg-site-danger/10 px-4 py-3 text-sm text-site-danger">{registerError}</div>}
                    {registerSuccess && mode === 'register' && <div className="rounded-2xl border border-site-success/22 bg-site-success/10 px-4 py-3 text-sm text-site-success">{registerSuccess}</div>}
                  </div>
                )}

                {capabilities.social_auth_enabled && Object.values(capabilities.social_providers).some(Boolean) && (
                  <div className="rounded-[26px] border border-site-border bg-site-surface/[0.035] p-4">
                    <SocialAuthButtons intent={mode} returnTo={returnTo} providers={capabilities.social_providers} />
                  </div>
                )}

                {hasModeEmailChannel && (
                  <div className="my-6 flex items-center gap-4">
                    <div className="h-px flex-1 bg-site-text/8" />
                    <span className="text-[11px] uppercase tracking-[0.28em] text-site-muted">Email</span>
                    <div className="h-px flex-1 bg-site-text/8" />
                  </div>
                )}

                {mode === 'login' && capabilities.legacy_login_enabled && (
                  <form onSubmit={handleLoginSubmit} className="space-y-4">
                    <div className="grid gap-4 sm:grid-cols-2">
                      <div className="sm:col-span-2">
                        <label className="mb-2 block text-[11px] uppercase tracking-[0.3em] text-site-muted">Логин, email или nickname</label>
                        <input
                          type="text"
                          value={loginData.username}
                          onChange={(event) => {
                            setLoginData((current) => ({ ...current, username: event.target.value }));
                            setLoginError('');
                          }}
                          disabled={loginLoading}
                          className={panelInputClass}
                          placeholder="Например, lab.user@kbsu.ru"
                          autoComplete="username"
                        />
                      </div>
                      <div className="sm:col-span-2">
                        <label className="mb-2 block text-[11px] uppercase tracking-[0.3em] text-site-muted">Пароль</label>
                        <div className="relative">
                          <input
                            type={showLoginPassword ? 'text' : 'password'}
                            value={loginData.password}
                            onChange={(event) => {
                              setLoginData((current) => ({ ...current, password: event.target.value }));
                              setLoginError('');
                            }}
                            disabled={loginLoading}
                            className={cn(panelInputClass, 'pr-14')}
                            placeholder="Введите пароль"
                            autoComplete="current-password"
                          />
                          <button
                            type="button"
                            onClick={() => setShowLoginPassword((current) => !current)}
                            className="absolute inset-y-0 right-0 inline-flex w-14 items-center justify-center text-xs uppercase tracking-[0.18em] text-site-muted transition hover:text-site-text"
                          >
                            {showLoginPassword ? 'Hide' : 'Show'}
                          </button>
                        </div>
                      </div>
                    </div>

                    <div className="flex flex-col gap-3 text-sm text-site-muted sm:flex-row sm:items-center sm:justify-between">
                      <label className="inline-flex items-center gap-3">
                        <input type="checkbox" checked={rememberMe} onChange={(event) => setRememberMe(event.target.checked)} className="h-4 w-4 rounded border-site-border bg-site-text/5" />
                        <span>Запомнить сессию на этом устройстве</span>
                      </label>
                      <Link href="/auth/forgot-password" className="text-site-accent transition hover:text-site-accent">Забыли пароль?</Link>
                    </div>

                    <Button
                      type="submit"
                      variant="primary"
                      size="lg"
                      loading={loginLoading}
                      disabled={loginLoading}
                      className="w-full rounded-[22px] border-0 bg-site-accent py-3.5 text-base font-semibold text-site-on-accent shadow-[0_20px_40px_rgba(34,211,238,0.18)] hover:brightness-105"
                    >
                      {loginLoading ? 'Подключаем профиль...' : 'Войти'}
                    </Button>
                  </form>
                )}

                {mode === 'register' && !capabilities.email_registration_disabled && (
                  <div className="space-y-4">
                    <div className="grid grid-cols-3 gap-2">
                      {['Email', 'Verify', 'Ready'].map((label, index) => {
                        const stepIndex = registerStep === 'email' ? 0 : registerStep === 'verify' ? 1 : 2;
                        const active = index <= stepIndex;
                        return (
                          <div
                            key={label}
                            className={cn(
                              'rounded-[18px] border px-3 py-2 text-center text-[11px] uppercase tracking-[0.24em] transition-all',
                              active ? 'border-site-accent/35 bg-site-accent/12 text-site-accent' : 'border-site-border bg-site-surface/[0.03] text-site-muted'
                            )}
                          >
                            {label}
                          </div>
                        );
                      })}
                    </div>

                    {registerStep === 'email' && (
                      <form onSubmit={handleRegisterCodeRequest} className="space-y-4">
                        <div>
                          <label className="mb-2 block text-[11px] uppercase tracking-[0.3em] text-site-muted">Email</label>
                          <input
                            type="email"
                            value={registerEmail}
                            onChange={(event) => {
                              setRegisterEmail(event.target.value);
                              setRegisterError('');
                            }}
                            disabled={registerLoading}
                            className={panelInputClass}
                            placeholder="mail@kbsu.ru"
                            autoComplete="email"
                          />
                        </div>

                        <Button
                          type="submit"
                          variant="primary"
                          size="lg"
                          loading={registerLoading}
                          disabled={registerLoading}
                          className="w-full rounded-[22px] border-0 bg-site-accent py-3.5 text-base font-semibold text-site-on-accent shadow-[0_20px_40px_rgba(45,212,191,0.18)] hover:brightness-105"
                        >
                          {registerLoading ? 'Отправляем код...' : 'Получить код'}
                        </Button>
                      </form>
                    )}

                    {registerStep === 'verify' && (
                      <form onSubmit={handleRegisterSubmit} className="space-y-4">
                        <div className="rounded-[22px] border border-site-border bg-site-surface/[0.035] px-4 py-3 text-sm text-site-secondary">
                          Код отправлен на <span className="font-medium text-site-text">{registerEmail}</span>
                        </div>

                        <div>
                          <label className="mb-2 block text-[11px] uppercase tracking-[0.3em] text-site-muted">Код подтверждения</label>
                          <input
                            type="text"
                            value={registerCode}
                            onChange={(event) => {
                              setRegisterCode(event.target.value.replace(/\D/g, '').slice(0, 6));
                              setRegisterError('');
                            }}
                            disabled={registerLoading}
                            className={cn(panelInputClass, 'text-center text-lg tracking-[0.42em]')}
                            placeholder="000000"
                            autoComplete="one-time-code"
                          />
                        </div>

                        <div>
                          <label className="mb-2 block text-[11px] uppercase tracking-[0.3em] text-site-muted">Пароль</label>
                          <div className="relative">
                            <input
                              type={showRegisterPassword ? 'text' : 'password'}
                              value={registerPassword}
                              onChange={(event) => {
                                setRegisterPassword(event.target.value);
                                setRegisterError('');
                              }}
                              disabled={registerLoading}
                              className={cn(panelInputClass, 'pr-14')}
                              placeholder="Минимум 8 символов"
                              autoComplete="new-password"
                            />
                            <button
                              type="button"
                              onClick={() => setShowRegisterPassword((current) => !current)}
                              className="absolute inset-y-0 right-0 inline-flex w-14 items-center justify-center text-xs uppercase tracking-[0.18em] text-site-muted transition hover:text-site-text"
                            >
                              {showRegisterPassword ? 'Hide' : 'Show'}
                            </button>
                          </div>
                        </div>

                        <div>
                          <label className="mb-2 block text-[11px] uppercase tracking-[0.3em] text-site-muted">Подтверждение пароля</label>
                          <input
                            type={showRegisterPassword ? 'text' : 'password'}
                            value={registerPasswordConfirm}
                            onChange={(event) => {
                              setRegisterPasswordConfirm(event.target.value);
                              setRegisterError('');
                            }}
                            disabled={registerLoading}
                            className={panelInputClass}
                            placeholder="Повторите пароль"
                            autoComplete="new-password"
                          />
                        </div>

                        <div className="grid gap-3 sm:grid-cols-2">
                          <Button
                            type="submit"
                            variant="primary"
                            size="lg"
                            loading={registerLoading}
                            disabled={registerLoading}
                            className="w-full rounded-[22px] border-0 bg-site-accent py-3.5 text-base font-semibold text-site-on-accent shadow-[0_20px_40px_rgba(45,212,191,0.18)] hover:brightness-105"
                          >
                            {registerLoading ? 'Создаём профиль...' : 'Завершить'}
                          </Button>
                          <Button
                            type="button"
                            variant="outline"
                            size="lg"
                            className="w-full rounded-[22px] border-site-border bg-site-surface/[0.03] text-site-text hover:bg-site-surface/[0.06]"
                            onClick={() => {
                              setRegisterStep('email');
                              setRegisterCode('');
                              setRegisterToken('');
                              setRegisterError('');
                              setRegisterSuccess('');
                              setRegisteredUsername('');
                            }}
                          >
                            Изменить email
                          </Button>
                        </div>
                      </form>
                    )}

                    {registerStep === 'done' && (
                      <div className="space-y-4 rounded-[26px] border border-site-success/18 bg-site-success/8 p-5">
                        <div className="flex items-center gap-3">
                          <span className="inline-flex h-11 w-11 items-center justify-center rounded-2xl bg-site-success/14 text-site-success">
                            <ShieldIcon className="h-5 w-5" />
                          </span>
                          <div>
                            <div className="text-lg font-semibold text-site-text">Профиль активирован</div>
                            <div className="text-sm text-site-success/80">Письмо содержит логин, но не пароль.</div>
                            {registeredUsername && (
                              <div className="mt-2 text-sm text-site-muted">Ваш логин: <span className="font-medium text-site-text">{registeredUsername}</span></div>
                            )}
                          </div>
                        </div>
                        <Link
                          href="/login"
                          className="inline-flex w-full items-center justify-center rounded-[22px] bg-site-surface px-4 py-3.5 text-base font-semibold text-site-muted transition hover:brightness-105"
                        >
                          Перейти ко входу
                        </Link>
                      </div>
                    )}
                  </div>
                )}

                {!hasModeEmailChannel && (!capabilities.social_auth_enabled || !Object.values(capabilities.social_providers).some(Boolean)) && (
                  <div className="rounded-[24px] border border-site-border bg-site-surface/[0.03] px-4 py-5 text-sm leading-6 text-site-secondary">
                    Email и social-авторизация сейчас недоступны. Проверьте настройки auth-модулей на сервере.
                  </div>
                )}

                <div className="mt-5">
                  <Link
                    href="/"
                    className="inline-flex h-12 w-full items-center justify-center rounded-[18px] border border-site-accent/34 bg-site-surface px-4 py-2 text-sm font-semibold tracking-[0.01em] text-site-text shadow-[0_14px_36px_rgba(34,211,238,0.18)] transition hover:translate-y-[-2px] hover:border-site-accent/45 hover:shadow-[0_18px_42px_rgba(56,189,248,0.28)] active:scale-[0.985] md:text-base"
                  >
                    ← Вернуться на главную
                  </Link>
                </div>
              </div>
            </div>
          </section>
        </div>
      </div>
    </div>
  );
}
